import type {
  IgPreflightFinding,
  IgPreflightResult,
} from "../../../../api/post";
import type { PreflightMediaFinding } from "../../../../features/post/validation";

/**
 * What the composer shows for one preflight answer.
 *
 * Split by where it belongs rather than by severity: the two media lists hang
 * off the asset tiles, the two form lists render beside the Instagram controls.
 */
export interface PreflightNotices {
  formErrors: string[];
  formWarnings: string[];
  mediaErrors: PreflightMediaFinding[];
  mediaWarnings: PreflightMediaFinding[];
}

export const UNAVAILABLE_MESSAGE =
  "We could not check how this post will publish on Instagram. Anything wrong with it will show up when it publishes.";

export const PHONE_TRACK_LEAD =
  "This post will publish from the Sked Social phone app for these accounts, rather than through Instagram's API.";

const emptyNotices = (): PreflightNotices => ({
  formErrors: [],
  formWarnings: [],
  mediaErrors: [],
  mediaWarnings: [],
});

/**
 * Whether a blocker finding may refuse the save.
 *
 * EVERY selected account, deliberately, and deliberately unlike the capability
 * gate on the controls, which asks ANY. The preflight is advisory and the server
 * refuses nothing: a post the API cannot express still publishes from a phone,
 * so blocking a selection that has a phone account in it would refuse a post
 * that would have gone out fine.
 *
 * An empty account list answers false. `every` on an empty array is true, and an
 * empty list is what an unavailable check and a 403 both carry.
 */
export const preflightBlocksSave = (
  preflight: IgPreflightResult | undefined
): boolean =>
  Boolean(
    preflight &&
    !preflight.unavailable &&
    preflight.accounts.length > 0 &&
    preflight.accounts.every((account) => account.apiCapable)
  );

/**
 * Identical sentences share one entry.
 *
 * The server reports per media item, so a carousel of three broken videos
 * carries the same sentence three times, and three copies of one line is how a
 * customer learns to stop reading them.
 */
const groupByMessage = (
  findings: IgPreflightFinding[]
): PreflightMediaFinding[] => {
  const byMessage = new Map<string, number[]>();

  for (const finding of findings) {
    const index = finding.mediaIndex as number;
    const existing = byMessage.get(finding.message);

    if (existing) {
      if (!existing.includes(index)) existing.push(index);
    } else {
      byMessage.set(finding.message, [index]);
    }
  }

  return [...byMessage].map(([message, indexes]) => ({ message, indexes }));
};

const label = (login: string | undefined) =>
  login && login.trim() ? login : "One of the selected accounts";

/**
 * SKE-4544. Turns a preflight answer into the messages the composer renders.
 *
 * Every sentence here comes from the server. It ships customer-facing wording in
 * `message` and re-deriving it on the client would put a second copy of Meta's
 * rules in the product, which is the drift this endpoint exists to avoid.
 *
 * Three answers, not two:
 *
 * - findings, the normal case;
 * - `unavailable`, the server's own soft-fail when its preflight throws, which
 *   says the check did not run rather than that the post is fine;
 * - an account the server could not reach Instagram for, which answers 403 for
 *   the WHOLE request and suppresses the findings for the healthy accounts too.
 *   The saga records that reconnect sentence as an unavailable result carrying
 *   one finding, so it arrives here as the second case with something to say.
 */
export const preflightNotices = (
  preflight: IgPreflightResult | undefined
): PreflightNotices => {
  const notices = emptyNotices();

  if (!preflight) return notices;

  if (preflight.unavailable) {
    for (const finding of preflight.postFindings ?? []) {
      notices.formWarnings.push(finding.message);
    }
    notices.formWarnings.push(UNAVAILABLE_MESSAGE);
    return notices;
  }

  const blocks = preflightBlocksSave(preflight);
  const mediaBlockers: IgPreflightFinding[] = [];
  const mediaWarnings: IgPreflightFinding[] = [];

  for (const finding of preflight.postFindings ?? []) {
    const isBlocker = finding.severity === "blocker";

    if (typeof finding.mediaIndex === "number") {
      (isBlocker && blocks ? mediaBlockers : mediaWarnings).push(finding);
      continue;
    }

    (isBlocker && blocks ? notices.formErrors : notices.formWarnings).push(
      finding.message
    );
  }

  notices.mediaErrors = groupByMessage(mediaBlockers);
  notices.mediaWarnings = groupByMessage(mediaWarnings);

  /*
   * Account findings are never errors, whatever severity they carry. They say
   * the API cannot express this post on that account, which is the dual-track
   * behaviour working rather than a defect: the post publishes, from a phone.
   */
  const accountLines = (preflight.accounts ?? []).flatMap((account) =>
    account.findings.map(
      (finding) => `${label(account.login)}: ${finding.message}`
    )
  );

  if (accountLines.length > 0) {
    notices.formWarnings.push(PHONE_TRACK_LEAD, ...accountLines);
  }

  return notices;
};
